import { useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, LayersControl, Polygon, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const { BaseLayer } = LayersControl;

function FitToParcelles({ parcelles }) {
  const map = useMap();
  useEffect(() => {
    const points = parcelles
      .map(p => p.centroid)
      .filter(c => c && c.lat && c.lon)
      .map(c => [c.lat, c.lon]);
    if (points.length > 0) {
      try {
        const bounds = points.reduce((b, p) => {
          const lat = p[0], lon = p[1];
          return {
            minLat: Math.min(b.minLat, lat),
            maxLat: Math.max(b.maxLat, lat),
            minLon: Math.min(b.minLon, lon),
            maxLon: Math.max(b.maxLon, lon),
          };
        }, { minLat: 90, maxLat: -90, minLon: 180, maxLon: -180 });
        const lat = (bounds.minLat + bounds.maxLat) / 2;
        const lon = (bounds.minLon + bounds.maxLon) / 2;
        const zoom = points.length > 1 ? 13 : 15;
        map.setView([lat, lon], zoom);
      } catch (e) {
        // fallback Saint-Romain-en-Viennois
        map.setView([44.2719, 5.0789], 13);
      }
    } else {
      map.setView([44.2719, 5.0789], 13);
    }
  }, [parcelles, map]);
  return null;
}

function colorForCulture(culture = '') {
  const c = culture.toLowerCase();
  if (c.includes('vigne') || c.includes('raisin')) return '#a855f7'; // violet
  if (c.includes('oliv')) return '#84cc16'; // lime
  if (c.includes('lavande') || c.includes('lavandin')) return '#7c3aed';
  if (c.includes('céréale') || c.includes('blé') || c.includes('cereale')) return '#f59e0b';
  if (c.includes('jachère') || c.includes('jachere')) return '#94a3b8';
  return '#10b981'; // emerald default
}

export default function ParcellesMap({ parcelles, onParcelleClick, onAskIntervention }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-white/10" style={{ height: 480 }}>
      <MapContainer
        center={[44.2719, 5.0789]}
        zoom={13}
        scrollWheelZoom
        style={{ height: '100%', width: '100%', background: '#0f172a' }}
      >
        <FitToParcelles parcelles={parcelles} />
        <LayersControl position="topright">
          <BaseLayer checked name="Vue satellite (Esri)">
            <TileLayer
              attribution="Tiles © Esri"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </BaseLayer>
          <BaseLayer name="Plan classique">
            <TileLayer
              attribution="© OpenStreetMap"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
          </BaseLayer>
        </LayersControl>

        {parcelles.map((p) => {
          const c = p.centroid;
          if (!c || !c.lat) return null;
          const color = colorForCulture(p.culture);
          const nom = p.nom_parcelle || p.nom || 'Parcelle';
          const isBio = p.conduite_bio || p.mode_conduite === 'biologique';

          // Si geometry GeoJSON Polygon, on l'affiche
          if (p.geometry && p.geometry.type === 'Polygon' && p.geometry.coordinates?.[0]) {
            const positions = p.geometry.coordinates[0].map((co) => [co[1], co[0]]);
            return (
              <Polygon
                key={p.id}
                positions={positions}
                pathOptions={{
                  color: '#fff',
                  fillColor: color,
                  fillOpacity: 0.55,
                  weight: 2.5,
                  dashArray: isBio ? null : '4 6',
                }}
                eventHandlers={{ click: () => onParcelleClick && onParcelleClick(p) }}
              >
                <Popup>
                  <ParcellePopup p={p} nom={nom} isBio={isBio} onAsk={() => onAskIntervention && onAskIntervention(p)} />
                </Popup>
              </Polygon>
            );
          }

          // Sinon CircleMarker au centroïde
          return (
            <CircleMarker
              key={p.id}
              center={[c.lat, c.lon]}
              radius={8 + Math.min(parseFloat(p.surface_ha || 0) * 4, 14)}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.65, weight: 2 }}
            >
              <Popup>
                <ParcellePopup p={p} nom={nom} isBio={isBio} onAsk={() => onAskIntervention && onAskIntervention(p)} approximatif />
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}

function ParcellePopup({ p, nom, isBio, onAsk, approximatif }) {
  return (
    <div style={{ minWidth: 200, fontFamily: 'system-ui' }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: '#0f172a' }}>
        {nom} {isBio && <span style={{ background: '#10b981', color: 'white', padding: '1px 6px', borderRadius: 3, fontSize: 10, marginLeft: 4 }}>BIO</span>}
      </div>
      <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
        <div><strong>Surface :</strong> {p.surface_ha || 0} ha</div>
        {p.culture && <div><strong>Culture :</strong> {p.culture}</div>}
        {p.variete && <div><strong>Variété :</strong> {p.variete}</div>}
        {p.commune && <div><strong>Commune :</strong> {p.commune}</div>}
        {approximatif && (
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>
            Position approximative (centroïde commune)
          </div>
        )}
      </div>
      {onAsk && (
        <button
          onClick={onAsk}
          style={{
            marginTop: 8,
            width: '100%',
            background: 'linear-gradient(to right, #84cc16, #10b981)',
            color: '#064e3b',
            fontWeight: 700,
            border: 'none',
            padding: '6px 12px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          + Demander une intervention
        </button>
      )}
    </div>
  );
}
